import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Td,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaCopy,
  FaEllipsisV,
  FaFileDownload,
  FaFilePdf,
  FaPaperclip,
  FaPlus,
  FaSignature,
  FaTimes,
} from "react-icons/fa";
import { SignedDocumentDto } from "../../autogen/bff-api";
import { requireBooleanEnvVar } from "../../config";
import { SignedDocumentStatusTag } from "../../pages/contracts/edit/docs/signatures/SignedDocumentStatusTag";
import { useApiError } from "../errors/useApiError";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import { Logo } from "../Logo";
import { Explanation } from "../support/Explanation/Explanation";
import { isAiAnalysable } from "./CombinedDocuments";
import { useDocumentDownloader } from "./DownloadDocumentIconButton/useDocumentDownloader";

export const SignedDocumentRow = ({
  doc,
  isAiChatEnabled,
  setDocumentForAnalysis,
  setDocumentForFencyModal,
  setDocumentForAttachmentsModal,
  setDocumentForSignersModal,
  setDocumentForMergedDocumentsModal,
  isTagsColumnVisible,
  isCategoryColumnVisible = false,
  canEditTags,
  onUpdateTags,
  isCreatedByColumnVisible,
  setPreviewPdfUrl,
}: {
  doc: SignedDocumentDto;
  isAiChatEnabled: boolean;
  setDocumentForAnalysis: (doc: SignedDocumentDto | undefined) => void;
  setDocumentForFencyModal: (doc: SignedDocumentDto) => void;
  setDocumentForAttachmentsModal: (doc: SignedDocumentDto | undefined) => void;
  setDocumentForSignersModal: (doc: SignedDocumentDto | undefined) => void;
  setDocumentForMergedDocumentsModal: (doc: SignedDocumentDto | undefined) => void;
  isTagsColumnVisible: boolean;
  isCategoryColumnVisible?: boolean;
  canEditTags: boolean;
  onUpdateTags: ({ document, tags }: { document: SignedDocumentDto; tags: string[] }) => void;
  isCreatedByColumnVisible: boolean;
  setPreviewPdfUrl: (url: string) => void;
}) => {
  const { t } = useTranslation();
  const fencyDocumentChatEnabled = requireBooleanEnvVar("VITE_ENABLE_FENCY_DOCUMENT_CHAT");
  const displayer = useApiError();

  const { getDownloadLink } = useDocumentDownloader();

  const [isCreatingDownloadLink, setIsCreatingDownloadLink] = useState<boolean>(false);
  const [isCreatingPreviewLink, setIsCreatingPreviewLink] = useState<boolean>(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [newTag, setNewTag] = useState<string>();

  const hasMergedDocuments = !!doc.mergedDocuments;

  return (
    <Tr key={doc.id}>
      <Td px="3">
        <Input
          size="sm"
          rounded="md"
          fontWeight="normal"
          minWidth={{ base: "60", lg: "80" }}
          value={doc.name}
          _focus={{
            borderColor: "default",
            boxShadow: "default",
          }}
          _disabled={{ border: "none" }}
          isDisabled={true}
        />
      </Td>
      <Td fontSize="sm">{displayDateWithTime(doc.uploadedAt)}</Td>
      {isCreatedByColumnVisible && (
        <Td fontSize="sm">{`${doc.sentForSigningBy?.firstName} ${doc.sentForSigningBy?.lastName}`}</Td>
      )}
      {isTagsColumnVisible && (
        <Td>
          <Flex flexWrap="wrap" columnGap="1" rowGap="1" width="48" maxWidth="48">
            <SignedDocumentStatusTag status={doc.status} />
            {doc.tags.map((tag, i) => (
              <Tag key={`${tag}-${i}`} size="sm" backgroundColor="smSecondarySoft" onClick={(e) => e.stopPropagation()}>
                <TagLabel>{tag}</TagLabel>
                {canEditTags && (
                  <TagCloseButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateTags({
                        document: doc,
                        tags: doc.tags.filter((_, j) => i !== j),
                      });
                      setNewTag(undefined);
                    }}
                  />
                )}
              </Tag>
            ))}
            {canEditTags && newTag === undefined && (
              <IconButton
                aria-label="add tag"
                variant="ghost"
                size="xs"
                colorScheme="cyan"
                icon={<Icon as={FaPlus} />}
                onClick={() => setNewTag("")}
              />
            )}
            {newTag !== undefined && (
              <InputGroup display="inline-flex" width="40">
                <Input
                  size="xs"
                  rounded="md"
                  pr="6"
                  autoFocus={true}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                />
                <InputRightElement width="12" height="6">
                  <IconButton
                    height="4"
                    size="xs"
                    aria-label="save tag"
                    icon={<Icon as={FaTimes} />}
                    rounded="none"
                    variant="ghost"
                    color="smMuted"
                    p="0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewTag(undefined);
                    }}
                  />
                  <IconButton
                    size="xs"
                    aria-label="save tag"
                    icon={<Icon as={FaCheck} />}
                    roundedLeft="none"
                    colorScheme="teal"
                    onClick={async () => {
                      onUpdateTags({ document: doc, tags: [...(doc.tags ?? []), newTag] });
                      setNewTag(undefined);
                    }}
                  />
                </InputRightElement>
              </InputGroup>
            )}
          </Flex>
        </Td>
      )}
      {isCategoryColumnVisible && <Td />}
      <Td>
        <Menu
          isOpen={isActionMenuOpen}
          onOpen={() => setIsActionMenuOpen(true)}
          onClose={() => {
            setIsActionMenuOpen(false);
          }}
          closeOnSelect={false}
        >
          <MenuButton
            aria-label="actions"
            as={IconButton}
            size="sm"
            variant="ghost"
            icon={<Icon as={FaEllipsisV} />}
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList>
            <MenuItem
              isDisabled={!doc.signingCompleted}
              icon={<Icon as={FaFilePdf} />}
              onClick={async (e) => {
                e.stopPropagation();
                setIsCreatingPreviewLink(true);
                const link = await getDownloadLink(doc.id, true).catch((e) => {
                  displayer.trigger({ message: `could not generate preview link, response: ${link}` });
                  setIsCreatingPreviewLink(false);
                  throw e;
                });
                setIsCreatingPreviewLink(false);
                if (link) setPreviewPdfUrl(link);
                setIsActionMenuOpen(false);
              }}
            >
              {t("Preview")} {isCreatingPreviewLink && <Spinner size="sm" ml="2" />}
            </MenuItem>
            <MenuItem
              isDisabled={!doc.signingCompleted}
              icon={<Icon as={FaFileDownload} />}
              onClick={async (e) => {
                e.stopPropagation();
                setIsCreatingDownloadLink(true);
                const link = await getDownloadLink(doc.id, true).catch((e) => {
                  displayer.trigger({ message: `could not generate download link` });
                  setIsCreatingDownloadLink(false);
                  throw e;
                });
                setIsCreatingDownloadLink(false);
                if (link) {
                  window.open(link, "_blank", "noopener,noreferrer");
                }
                setIsActionMenuOpen(false);
              }}
            >
              {t("Download")} {isCreatingDownloadLink && <Spinner size="sm" ml="2" />}
            </MenuItem>
            {isAiChatEnabled && (
              <>
                <Box as="span" display="block">
                  <Explanation
                    text={isAiAnalysable(doc) ? t("Chat with document") : t("Can only chat with PDFs")}
                    shouldWrapChildren
                  >
                    <MenuItem
                      isDisabled={!isAiAnalysable(doc) || !doc.signingCompleted}
                      aria-label="analyze with AI"
                      color="smPrimary"
                      icon={<Logo />}
                      onClick={() => setDocumentForAnalysis(doc)}
                    >
                      {t("Chat with document")}
                    </MenuItem>
                  </Explanation>
                </Box>
                {fencyDocumentChatEnabled && (
                  <Box as="span" display="block">
                    <Explanation
                      text={isAiAnalysable(doc) ? t("Chat with document (Fency)") : t("Can only chat with PDFs")}
                      shouldWrapChildren
                    >
                      <MenuItem
                        isDisabled={!isAiAnalysable(doc) || !doc.signingCompleted}
                        aria-label="chat with document Fency"
                        color="smPrimary"
                        icon={<Logo />}
                        onClick={() => {
                          setDocumentForFencyModal(doc);
                          setIsActionMenuOpen(false);
                        }}
                      >
                        {t("Chat with document (Fency)")}
                      </MenuItem>
                    </Explanation>
                  </Box>
                )}
              </>
            )}
            {doc.attachments?.length ? (
              <MenuItem
                aria-label="see attachments"
                icon={<Icon as={FaPaperclip} />}
                onClick={() => setDocumentForAttachmentsModal(doc)}
              >
                {t("See attachments")}
              </MenuItem>
            ) : null}
            {hasMergedDocuments && (
              <MenuItem
                icon={<Icon as={FaCopy} />}
                onClick={() => {
                  setDocumentForMergedDocumentsModal(doc);
                  setIsActionMenuOpen(false);
                }}
              >
                {t("Merged documents")}
              </MenuItem>
            )}
            <MenuItem
              icon={<Icon as={FaSignature} w="15px" h="15px" />}
              onClick={() => setDocumentForSignersModal(doc)}
            >
              {t("View signers")}
            </MenuItem>
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
};
